import React, { useState, useEffect, useRef } from 'react';
import '../../../styles/homepagecss/Textractor.css';

const HomeTextractor = () => {
  const [currentDocIndex, setCurrentDocIndex] = useState(0);
  const [tableRows, setTableRows] = useState([]);
  const [highlightedField, setHighlightedField] = useState(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const animationTimeoutsRef = useRef([]);

  // Updated column headers for extraction
  const headers = [
    "Author/Year",
    "Study Design", 
    "Population", 
    "Intervention",
    "Comparator",
    "Key Findings"
  ];
  
  // Enhanced SLR document data structure with new fields
  const documents = [
    {
      fileName: "Johnson_2023_SLR.pdf",
      title: "Efficacy of CBT for Treatment-Resistant Depression",
      author: "Johnson et al., 2023",
      design: "Randomized Controlled Trial",
      population: "124 adults with treatment-resistant depression",
      intervention: "16 weekly sessions of CBT plus treatment as usual (TAU)",
      comparator: "Treatment as usual (TAU) alone",
      outcome: "Multi-center RCT of CBT for treatment-resistant depression (124 adults). Participants were assigned to either 16 weekly sessions of CBT plus TAU or TAU alone. CBT group showed 62% response rate vs 29% in TAU. HDRS score reduction: 8.7 points (p=0.003). QoL improved (SF-36 +15.2 vs +4.8, p<0.001) and suicidal ideation reduced (56% vs 22%, p=0.007).",
      keyFindings: "CBT: 62% response rate vs 29% TAU. HDRS reduction: 8.7 points (p=0.003)."
    },
    {
      fileName: "Chang_2024_Depression.pdf",
      title: "Online vs. In-Person CBT for Depression",
      author: "Chang et al., 2024",
      design: "Prospective Cohort Study", 
      population: "287 participants with moderate depression (PHQ-9: 10-19)",
      intervention: "Online CBT (12 sessions over 16 weeks)",
      comparator: "In-person CBT (12 sessions over 16 weeks)",
      outcome: "Comparative study across 6 clinics with 287 participants with moderate depression. Online CBT non-inferior to in-person with PHQ-9 reduction difference of 1.2% (p=0.42). Remission rates: 52% vs 54%. Online delivery cost $267 less per QALY with higher completion rates (83% vs 71%, p=0.04), especially in rural and younger participants.",
      keyFindings: "Online CBT non-inferior to in-person. Higher completion (83% vs 71%, p=0.04)."
    },
    {
      fileName: "Martinez_2022_Meta.pdf",
      title: "Meta-Analysis of Digital Interventions",
      author: "Martinez et al., 2022", 
      design: "Systematic Review and Meta-Analysis",
      population: "42 studies (N=5,819 participants across 14 countries)",
      intervention: "Digital mental health interventions",
      comparator: "Various controls (waitlist, TAU, active controls)",
      outcome: "Meta-analysis of 42 RCTs (5,819 participants). Digital mental health interventions showed moderate effect size (g=0.58, 95% CI 0.49-0.67, I²=68%). Guided interventions (g=0.67) outperformed self-guided programs (g=0.34, p<0.001). Anxiety interventions more effective (g=0.63) than depression (g=0.54).",
      keyFindings: "Moderate effect size (g=0.58). Guided > self-guided interventions."
    },
    {
      fileName: "Taylor_2023_Anxiety.pdf",
      title: "MBCT for Recurrent Anxiety",
      author: "Taylor et al., 2023",
      design: "Longitudinal Case-Control Study",
      population: "198 patients with recurrent GAD (63% female, mean age 36.4)",
      intervention: "8-week Mindfulness-Based Cognitive Therapy (MBCT)",
      comparator: "Maintenance pharmacotherapy",
      outcome: "24-month follow-up of 198 GAD patients using MBCT (n=97) vs pharmacotherapy (n=101). MBCT showed 47% lower relapse rate (HR=0.53, p<0.001). QoL improved (+7.4 points, p=0.002) with 68% reduction in medication use vs 24% in controls. MBCT particularly effective for comorbid depression (relapse: 23% vs 58%, p<0.001).",
      keyFindings: "47% lower relapse with MBCT vs medication (HR=0.53, p<0.001)."
    }
  ];

  // Clear all timeouts on unmount
  useEffect(() => {
    return () => {
      animationTimeoutsRef.current.forEach(timeout => clearTimeout(timeout));
    };
  }, []);

  // Run the animation when document changes
  useEffect(() => {
    // Clear previous timeouts
    animationTimeoutsRef.current.forEach(timeout => clearTimeout(timeout));
    animationTimeoutsRef.current = [];
    
    // Start extraction process
    setIsExtracting(true);
    
    // Extract each field with a delay
    const currentDoc = documents[currentDocIndex];
    const fieldsMap = {
      "Author/Year": currentDoc.author,
      "Study Design": currentDoc.design,
      "Population": currentDoc.population,
      "Intervention": currentDoc.intervention,
      "Comparator": currentDoc.comparator,
      "Key Findings": currentDoc.keyFindings
    };
    
    // For each field, highlight and add to table
    headers.forEach((field, index) => {
      const timeout = setTimeout(() => {
        setHighlightedField(field);
        
        // After highlighting, add to table row
        const addTimeout = setTimeout(() => {
          // If last field in the document
          if (index === headers.length - 1) {
            // Add the row to table
            setTableRows(prev => [...prev, fieldsMap]);
            
            // Reset highlight
            setHighlightedField(null);
            setIsExtracting(false);
            
            // After a pause, move to next document
            const nextTimeout = setTimeout(() => {
              const nextIndex = (currentDocIndex + 1) % documents.length;
              
              // If we're starting over, clear the table
              if (nextIndex === 0) {
                setTableRows([]);
              }
              
              setCurrentDocIndex(nextIndex);
            }, 1000);
            
            animationTimeoutsRef.current.push(nextTimeout);
          }
        }, 200);
        
        animationTimeoutsRef.current.push(addTimeout);
      }, 70 * (index + 1));
      
      animationTimeoutsRef.current.push(timeout);
    });
    
  }, [currentDocIndex]);

  return (
      <div className="container">
        <div class="text-center col-lg-8 col-md-9 col-12 m-auto">
             <span className="gradient-font8-animated ev-fs-25 col-lg-6 ev-bold-med">Think Extraction, Think Textractor.</span>
              <p className="ev-reg-p-dark ev-fs-14 ev-reg margin-bottom-5 margin-top-1">Textractor is the intelligent extraction system that helps you extract data from documents. It supports complete automation, getting your entire extraction grid ready in the time it takes to grab a coffee.</p>  

        </div>
        <div className="row ev-card-2 ev-padding-xlarge">

          <div className="text-center">
                <div className="row g-4 margin-bottom-5 col-lg-6 col-md-7 col-12 m-auto">
                    <div className="col-6">
                      <div className="gradient-font9-animated ev-bold-reg ev-fs-30">125x</div>
                      <div className="ev-reg-p-dark ev-fs-13 ev-reg"><span className="ev-bold-med">Faster extraction.</span> Process 500 documents in 1 hour versus 125 hours (over 3 weeks) manually.</div>
                    </div>
                    <div className="col-6">
                      <div className="gradient-font9-animated  ev-bold-reg ev-fs-30">100%</div>
                      <div className="ev-reg-p-dark ev-fs-13 ev-reg"><span className="ev-bold-med">Elimination</span>  of night and weekend extraction work to meet tight regulatory deadlines.</div>
                    </div>                    
                </div>
          </div>
          <div className="row m-auto">
            <div className="col-lg-4 col-md-4 col-sm-4 col-12 mb-2">
              {/* Document section */}
              <div className="textractor-document">
                <div className="textractor-document-header">
                  <div className="text-center"><i class="bi bi-file-pdf"></i> {documents[currentDocIndex].fileName}</div>
                </div>
                <div className="textractor-document-content scrollable-element vh-45">
                  <h3>{documents[currentDocIndex].title}</h3>
                  
                  <div className={`textractor-field ${highlightedField === 'Author/Year' ? 'textractor-field-highlight' : ''}`}>
                    <span className="textractor-field-label">Author/Year:</span>
                    <span className="textractor-field-value">{documents[currentDocIndex].author}</span>
                  </div>
                  
                  <div className={`textractor-field ${highlightedField === 'Study Design' ? 'textractor-field-highlight' : ''}`}>
                    <span className="textractor-field-label">Study Design:</span>
                    <span className="textractor-field-value">{documents[currentDocIndex].design}</span>
                  </div>
                  
                  <div className={`textractor-field ${highlightedField === 'Population' ? 'textractor-field-highlight' : ''}`}>
                    <span className="textractor-field-label">Population:</span>
                    <span className="textractor-field-value">{documents[currentDocIndex].population}</span>
                  </div>
                  
                  <div className={`textractor-field ${highlightedField === 'Intervention' ? 'textractor-field-highlight' : ''}`}>
                    <span className="textractor-field-label">Intervention:</span>
                    <span className="textractor-field-value">{documents[currentDocIndex].intervention}</span>
                  </div>
                  
                  <div className={`textractor-field ${highlightedField === 'Comparator' ? 'textractor-field-highlight' : ''}`}>
                    <span className="textractor-field-label">Comparator:</span>
                    <span className="textractor-field-value">{documents[currentDocIndex].comparator}</span>
                  </div>
                  
                  <div className={`textractor-field textractor-field-outcome text-justify ${highlightedField === 'Key Findings' ? 'textractor-field-highlight' : ''}`}>
                    <span className="textractor-field-label">Primary Outcome Results:</span>
                    <br />
                    <span className="textractor-field-value">{documents[currentDocIndex].outcome}</span>
                  </div>
                </div>
                
                {/* AI Processor */}
                <div className={`textractor-ai ${isExtracting ? 'textractor-ai-active' : ''}`}>LLM Agent</div>
              </div>
            </div>
            
            <div className="col-lg-8 col-md-8 col-sm-8 col-12">
            {/* Table section */}
            <div className="textractor-table-wrapper scrollable-element">
              <table className="textractor-table">
                <thead>
                  <tr>
                    {headers.map(header => (
                      <th 
                        key={header} 
                        className={highlightedField === header ? 'textractor-header-highlight' : ''}
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((row, rowIndex) => (
                    <tr key={rowIndex} className={rowIndex === tableRows.length - 1 ? 'textractor-row-new' : ''}>
                      {headers.map(header => (
                        <td key={`${rowIndex}-${header}`}>
                          {row[header]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </div>
          </div>
        </div>
      </div>
  );
};

export default HomeTextractor;